// src/features/marketResearch/components/sections/CompetitiveAnalysis.tsx
import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Paper,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Business,
  Star,
  TrendingUp,
  CompareArrows,
  LocationOn,
  Hotel,
  Assessment
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter
} from 'recharts';
import { SectionContainer, SectionTitle, SectionBody, SubSectionTitle } from '../Section';

interface CompetitiveData {
  competitiveSet?: string[];
  rateComparison?: Record<string, string>;
  marketPenetration?: string;
}

interface Props {
  data?: CompetitiveData;
}

export const CompetitiveAnalysis: React.FC<Props> = ({ data }) => {
  const setList = data?.competitiveSet ?? [];
  const rates = data?.rateComparison ?? {};
  const penetration = data?.marketPenetration || '';

  // Sample competitive positioning data
  const competitivePositioning = [
    { name: 'Subject Property', price: 285, quality: 8.5, marketShare: 12 },
    { name: 'Competitor A', price: 320, quality: 9.0, marketShare: 18 },
    { name: 'Competitor B', price: 265, quality: 7.8, marketShare: 15 },
    { name: 'Competitor C', price: 295, quality: 8.2, marketShare: 14 },
    { name: 'Competitor D', price: 310, quality: 8.8, marketShare: 11 }
  ];

  // Sample rate comparison data for charts
  const rateComparisonData = [
    { segment: 'Luxury', average: 450, subject: 420, competitor: 475 },
    { segment: 'Upscale', average: 285, subject: 295, competitor: 275 },
    { segment: 'Midscale', average: 185, subject: 190, competitor: 180 },
    { segment: 'Economy', average: 125, subject: 135, competitor: 115 }
  ];

  // Competitive performance radar data
  const performanceMetrics = [
    { metric: 'Price Competitiveness', subject: 85, average: 75 },
    { metric: 'Service Quality', subject: 88, average: 80 },
    { metric: 'Location Score', subject: 92, average: 78 },
    { metric: 'Brand Recognition', subject: 75, average: 85 },
    { metric: 'Amenities', subject: 90, average: 82 },
    { metric: 'Online Reviews', subject: 87, average: 79 }
  ];

  // Sample competitive set with detailed information
  const detailedCompetitors = [
    {
      name: 'Grand Metropolitan Hotel',
      category: 'Luxury',
      rooms: 425,
      adr: '$345',
      occupancy: '78%',
      revpar: '$269',
      rating: 4.5,
      distance: '0.3 mi'
    },
    {
      name: 'Business Plaza Inn',
      category: 'Upscale',
      rooms: 280,
      adr: '$285',
      occupancy: '82%',
      revpar: '$234',
      rating: 4.2,
      distance: '0.7 mi'
    },
    {
      name: 'City Center Suites',
      category: 'Upscale',
      rooms: 195,
      adr: '$265',
      occupancy: '85%',
      revpar: '$225',
      rating: 4.3,
      distance: '1.2 mi'
    },
    {
      name: 'Airport Gateway Hotel',
      category: 'Midscale',
      rooms: 320,
      adr: '$195',
      occupancy: '88%',
      revpar: '$172',
      rating: 4.0,
      distance: '2.1 mi'
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Luxury': return '#7b1fa2';
      case 'Upscale': return '#1976d2';
      case 'Midscale': return '#388e3c';
      case 'Economy': return '#f57c00';
      default: return '#666';
    }
  };

  return (
    <SectionContainer id="competitive-analysis">
      <SectionTitle>Competitive Analysis & Market Positioning</SectionTitle>
      <SectionBody>
        {/* Market Positioning Overview */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <SubSectionTitle>Competitive Positioning Matrix</SubSectionTitle>
          <Box className="chart-container" sx={{ height: 350, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart data={competitivePositioning}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  type="number"
                  dataKey="price"
                  name="Average Daily Rate"
                  domain={['dataMin - 20', 'dataMax + 20']}
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Average Daily Rate ($)', position: 'insideBottom', offset: -10 }}
                />
                <YAxis
                  type="number"
                  dataKey="quality"
                  name="Quality Score"
                  domain={[7, 9.5]}
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Quality Score', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{
                          backgroundColor: '#fff',
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {data.name}
                          </Typography>
                          <Typography variant="body2">ADR: ${data.price}</Typography>
                          <Typography variant="body2">Quality: {data.quality}</Typography>
                          <Typography variant="body2">Market Share: {data.marketShare}%</Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter
                  dataKey="marketShare"
                  fill="#1976d2"
                  stroke="#1976d2"
                  strokeWidth={2}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Rate Comparison Charts */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, height: 400 }}>
              <SubSectionTitle>Rate Comparison by Segment</SubSectionTitle>
              <Box className="chart-container" sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rateComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="segment" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8
                      }}
                    />
                    <Bar dataKey="subject" fill="#1976d2" name="Subject Property" />
                    <Bar dataKey="average" fill="#42a5f5" name="Market Average" />
                    <Bar dataKey="competitor" fill="#90caf9" name="Top Competitor" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, height: 400 }}>
              <SubSectionTitle>Performance Radar</SubSectionTitle>
              <Box className="chart-container" sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={performanceMetrics}>
                    <PolarGrid stroke="#e0e0e0" />
                    <PolarAngleAxis
                      tick={{ fontSize: 10 }}
                      dataKey="metric"
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fontSize: 10 }}
                    />
                    <Radar
                      name="Subject Property"
                      dataKey="subject"
                      stroke="#1976d2"
                      fill="#1976d2"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                    <Radar
                      name="Market Average"
                      dataKey="average"
                      stroke="#f57c00"
                      fill="#f57c00"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Detailed Competitive Set Table */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <SubSectionTitle>Competitive Set Analysis</SubSectionTitle>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rooms</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ADR</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Occupancy</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>RevPAR</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Distance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailedCompetitors.map((competitor, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: getCategoryColor(competitor.category),
                          fontSize: '0.875rem'
                        }}>
                          {competitor.name.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {competitor.name}
        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={competitor.category}
                        size="small"
                        sx={{
                          backgroundColor: `${getCategoryColor(competitor.category)}15`,
                          color: getCategoryColor(competitor.category),
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>{competitor.rooms}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {competitor.adr}
                    </TableCell>
                    <TableCell>{competitor.occupancy}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#388e3c' }}>
                      {competitor.revpar}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Star sx={{ fontSize: 16, color: '#ffa726' }} />
                        <Typography variant="body2">{competitor.rating}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{competitor.distance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Market Penetration Analysis */}
        <Paper sx={{ p: 3 }}>
          <SubSectionTitle>Market Penetration Assessment</SubSectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                {penetration || 'The subject property demonstrates strong competitive positioning within the local market. Market analysis indicates opportunities for strategic positioning improvements through targeted service enhancements and competitive pricing strategies.'}
        </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                  Key Competitive Advantages
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Location Premium', value: 'Prime downtown location' },
                    { label: 'Facility Quality', value: 'Recently renovated amenities' },
                    { label: 'Service Level', value: 'Full-service operations' },
                    { label: 'Brand Recognition', value: 'Established market presence' }
                  ].map((advantage, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{
                        p: 2,
                        borderLeft: '4px solid #1976d2',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {advantage.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {advantage.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                height: '100%'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Assessment sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Competitive Score
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      8.2
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Out of 10
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Market Position: <strong>Strong</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Risk Level: <strong>Low</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Growth Potential: <strong>High</strong>
                </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
        </Grid>
        </Paper>
      </SectionBody>
    </SectionContainer>
  );
};
